import React, { useEffect, useState } from 'react';
import { Globe, Plus, Trash2, ToggleLeft, ToggleRight, ChevronDown, ChevronRight, Pencil, PlugZap } from 'lucide-react';
import api from '../../services/api';
import alertService from '../../utils/alert';
import DashboardModuleShell from '../../components/DashboardModuleShell';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { Badge } from '../../components/ui/Badge';
import FeatureLockedPage from '../../components/FeatureLockedPage';

const BLANK = {
  name: '', baseUrl: '', authType: 'none', authKeyName: '', authValue: '', isActive: true,
  orderStatusPath: '', orderStatusField: '', orderTotalField: '',
  productListPath: '', productsArrayPath: '', productNameField: '', productPriceField: '',
  productSkuField: '', productImageField: '', productDescriptionField: '',
  productCurrencyField: '', defaultCurrency: 'INR', productAvailabilityField: '',
};

export default function ApiConnectorsPage() {
  const [connectors, setConnectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [planError, setPlanError] = useState(null);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testError, setTestError] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get('/support/api-connectors');
      setConnectors(data.data || []);
    } catch (err) {
      if (err.response?.status === 403) {
        setPlanError(err.response.data?.message || err.response.data?.error || 'This feature is not available on your current plan.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const set = key => val => setForm(f => ({ ...f, [key]: val }));

  const openCreate = () => { setEditing(null); setForm(BLANK); setTestResult(null); setTestError(null); setShowModal(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name, baseUrl: c.baseUrl, authType: c.authType,
      authKeyName: c.authKeyName || '', authValue: '',
      isActive: c.isActive,
      orderStatusPath: c.orderStatusPath || '', orderStatusField: c.orderStatusField || '',
      orderTotalField: c.orderTotalField || '', productListPath: c.productListPath || '',
      productsArrayPath: c.productsArrayPath || '', productNameField: c.productNameField || '',
      productPriceField: c.productPriceField || '',
      productSkuField: c.productSkuField || '', productImageField: c.productImageField || '',
      productDescriptionField: c.productDescriptionField || '', productCurrencyField: c.productCurrencyField || '',
      defaultCurrency: c.defaultCurrency || 'INR', productAvailabilityField: c.productAvailabilityField || '',
    });
    setTestResult(null);
    setTestError(null);
    setShowModal(true);
  };

  const testConnection = async () => {
    setTesting(true);
    setTestError(null);
    setTestResult(null);
    try {
      const { data } = await api.post('/support/api-connectors/test', { ...form, connectorId: editing?._id });
      setTestResult(data.data);
    } catch (err) {
      setTestError(err.response?.data?.error || 'Test failed');
    } finally {
      setTesting(false);
    }
  };

  const save = async () => {
    if (!form.name.trim() || !form.baseUrl.trim()) return;
    setSaving(true);
    try {
      const payload = { ...form };
      if (editing && !payload.authValue) delete payload.authValue;
      if (editing) {
        await api.put(`/support/api-connectors/${editing._id}`, payload);
      } else {
        await api.post('/support/api-connectors', payload);
      }
      setShowModal(false);
      load();
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!(await alertService.confirm('Delete?', 'Delete this API connector?', 'danger', 'Delete'))) return;
    await api.delete(`/support/api-connectors/${id}`);
    load();
  };

  const toggleActive = async (c) => {
    await api.put(`/support/api-connectors/${c._id}`, { isActive: !c.isActive });
    load();
  };

  if (planError) {
    return (
      <FeatureLockedPage
        title="API Connectors"
        subtitle="Connect your REST APIs so the support agent can look up order status and product information in real time."
        icon={Globe}
        featureName="API Connectors"
        description={planError}
      />
    );
  }

  return (
    <DashboardModuleShell
      title="API Connectors"
      subtitle="Connect your REST APIs so the support agent can look up order status and product information in real time."
      icon={Globe}
      actions={
        <Button variant="brand" icon={Plus} onClick={openCreate}>New Connector</Button>
      }
    >
      {loading ? (
        <div className="flex items-center justify-center h-64 text-[#706E6B] text-[13px]">Loading…</div>
      ) : connectors.length === 0 ? (
        <Card className="py-16 text-center">
          <Globe size={40} className="mx-auto mb-3 text-[#D8DDE6]" />
          <p className="font-bold text-[#080707]">No API connectors yet</p>
          <p className="text-[12px] text-[#706E6B] mt-1">Connect a REST API to let the agent answer order and product questions live.</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {connectors.map(c => (
            <Card key={c._id} className="p-0 overflow-hidden">
              <div className="flex items-center gap-3 p-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-bold text-[13px] text-[#080707]">{c.name}</p>
                    <Badge variant={c.isActive ? 'success' : 'slate'}>{c.isActive ? 'active' : 'inactive'}</Badge>
                  </div>
                  <p className="text-[11px] text-[#706E6B] mt-0.5 truncate">{c.baseUrl}</p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => toggleActive(c)} title={c.isActive ? 'Disable' : 'Enable'} className="p-2 text-[#706E6B] hover:text-[#080707] rounded-[4px] transition-colors">
                    {c.isActive ? <ToggleRight size={18} className="text-[#2E844A]" /> : <ToggleLeft size={18} />}
                  </button>
                  <button onClick={() => openEdit(c)} className="p-2 text-[#706E6B] hover:text-[#0176D3] hover:bg-[#F4F6F9] rounded-[4px] transition-colors" title="Edit">
                    <Pencil size={14} />
                  </button>
                  <button onClick={() => remove(c._id)} className="p-2 text-[#706E6B] hover:text-[#BA0517] hover:bg-[#FEF1EE] rounded-[4px] transition-colors" title="Delete">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>

              {/* Field mapping summary */}
              {(c.orderStatusPath || c.productListPath) && (
                <div className="border-t border-[#D8DDE6]">
                  <button
                    onClick={() => setExpanded(prev => ({ ...prev, [c._id]: !prev[c._id] }))}
                    className="flex items-center gap-2 px-4 py-2 text-[13px] font-semibold text-[#706E6B] hover:bg-[#F4F6F9] w-full text-left transition-colors"
                  >
                    {expanded[c._id] ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                    Field mappings
                  </button>
                  {expanded[c._id] && (
                    <div className="px-4 pb-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px]">
                      {c.orderStatusPath && <div className="bg-[#F4F6F9] rounded-[4px] border border-[#D8DDE6] p-2"><p className="text-[#706E6B]">Order status path</p><p className="font-mono font-bold text-[#080707] mt-0.5">{c.orderStatusPath}</p></div>}
                      {c.orderStatusField && <div className="bg-[#F4F6F9] rounded-[4px] border border-[#D8DDE6] p-2"><p className="text-[#706E6B]">Status field</p><p className="font-mono font-bold text-[#080707] mt-0.5">{c.orderStatusField}</p></div>}
                      {c.orderTotalField && <div className="bg-[#F4F6F9] rounded-[4px] border border-[#D8DDE6] p-2"><p className="text-[#706E6B]">Order total field</p><p className="font-mono font-bold text-[#080707] mt-0.5">{c.orderTotalField}</p></div>}
                      {c.productListPath && <div className="bg-[#F4F6F9] rounded-[4px] border border-[#D8DDE6] p-2"><p className="text-[#706E6B]">Product list path</p><p className="font-mono font-bold text-[#080707] mt-0.5">{c.productListPath}</p></div>}
                      {c.productsArrayPath && <div className="bg-[#F4F6F9] rounded-[4px] border border-[#D8DDE6] p-2"><p className="text-[#706E6B]">Products array path</p><p className="font-mono font-bold text-[#080707] mt-0.5">{c.productsArrayPath}</p></div>}
                      {c.productNameField && <div className="bg-[#F4F6F9] rounded-[4px] border border-[#D8DDE6] p-2"><p className="text-[#706E6B]">Product name field</p><p className="font-mono font-bold text-[#080707] mt-0.5">{c.productNameField}</p></div>}
                      {c.productPriceField && <div className="bg-[#F4F6F9] rounded-[4px] border border-[#D8DDE6] p-2"><p className="text-[#706E6B]">Product price field</p><p className="font-mono font-bold text-[#080707] mt-0.5">{c.productPriceField}</p></div>}
                    </div>
                  )}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editing ? 'Edit API Connector' : 'New API Connector'}
        icon={Globe}
        maxWidth="max-w-lg"
        footer={
          <>
            <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button
              variant="brand"
              loading={saving}
              disabled={!form.name.trim() || !form.baseUrl.trim()}
              onClick={save}
            >
              {editing ? 'Save Changes' : 'Create Connector'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Input label="Name *" value={form.name} onChange={e => set('name')(e.target.value)} />
          <Input
            label="Base URL *"
            value={form.baseUrl}
            onChange={e => set('baseUrl')(e.target.value)}
            placeholder="https://api.yourstore.com/v1"
          />
          <div className="space-y-2">
            <label className="text-[12px] font-medium ml-0.5" style={{ color: 'var(--color-text-main)' }}>Auth Type</label>
            <select
              value={form.authType}
              onChange={e => set('authType')(e.target.value)}
              className="w-full px-4 h-9 border border-[#D8DDE6] rounded-[4px] text-[13px] focus:outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3] transition-all"
            >
              <option value="none">None</option>
              <option value="bearer">Bearer Token</option>
              <option value="header">Custom Header</option>
              <option value="query">Query Param</option>
            </select>
          </div>
          {form.authType !== 'none' && (
            <>
              {(form.authType === 'header' || form.authType === 'query') && (
                <Input
                  label={form.authType === 'header' ? 'Header Name' : 'Param Name'}
                  value={form.authKeyName}
                  onChange={e => set('authKeyName')(e.target.value)}
                  placeholder={form.authType === 'header' ? 'X-Api-Key' : 'api_key'}
                />
              )}
              <Input
                label={editing ? 'New Secret (blank = keep existing)' : 'Secret / Token'}
                type="password"
                value={form.authValue}
                onChange={e => set('authValue')(e.target.value)}
              />
            </>
          )}

          <p className="text-[11px] font-bold text-[#706E6B] uppercase pt-2">Order Status Mapping</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="Order status endpoint path"
              value={form.orderStatusPath}
              onChange={e => set('orderStatusPath')(e.target.value)}
              placeholder="/orders/{id}"
            />
            <Input
              label="Status field"
              value={form.orderStatusField}
              onChange={e => set('orderStatusField')(e.target.value)}
              placeholder="status"
            />
            <Input
              label="Total amount field"
              value={form.orderTotalField}
              onChange={e => set('orderTotalField')(e.target.value)}
              placeholder="total_amount"
            />
          </div>

          <p className="text-[11px] font-bold text-[#706E6B] uppercase pt-2">Product List Mapping</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="Product list endpoint"
              value={form.productListPath}
              onChange={e => set('productListPath')(e.target.value)}
              placeholder="/products"
            />
            <Input
              label="Array path in response"
              value={form.productsArrayPath}
              onChange={e => set('productsArrayPath')(e.target.value)}
              placeholder="data.products"
            />
            <Input
              label="Product name field"
              value={form.productNameField}
              onChange={e => set('productNameField')(e.target.value)}
              placeholder="name"
            />
            <Input
              label="Product price field"
              value={form.productPriceField}
              onChange={e => set('productPriceField')(e.target.value)}
              placeholder="price"
            />
            <Input
              label="SKU / retailer ID field"
              value={form.productSkuField}
              onChange={e => set('productSkuField')(e.target.value)}
              placeholder="sku"
            />
            <Input
              label="Currency field (optional)"
              value={form.productCurrencyField}
              onChange={e => set('productCurrencyField')(e.target.value)}
              placeholder="currency"
            />
            <Input
              label="Default currency (fallback)"
              value={form.defaultCurrency}
              onChange={e => set('defaultCurrency')(e.target.value)}
              placeholder="INR"
            />
            <Input
              label="Image URL field (optional)"
              value={form.productImageField}
              onChange={e => set('productImageField')(e.target.value)}
              placeholder="image_url"
            />
            <Input
              label="Description field (optional)"
              value={form.productDescriptionField}
              onChange={e => set('productDescriptionField')(e.target.value)}
              placeholder="description"
            />
            <Input
              label="Availability field (optional)"
              value={form.productAvailabilityField}
              onChange={e => set('productAvailabilityField')(e.target.value)}
              placeholder="in_stock"
            />
          </div>

          <div className="pt-2 border-t border-[#D8DDE6] space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-[11px] font-bold text-[#706E6B] uppercase ">Test &amp; Adjust</p>
              <Button variant="outline" size="sm" icon={PlugZap} loading={testing} onClick={testConnection} disabled={!form.baseUrl.trim()}>
                Test Connection
              </Button>
            </div>

            {testError && (
              <p className="text-[12px] text-[#BA0517] bg-[#FEF1EE] border border-[#FDDDE3] rounded-[4px] p-2">{testError}</p>
            )}

            {testResult && (
              <div className="space-y-3">
                <div>
                  <p className="text-[11px] font-bold text-[#706E6B] mb-1">Raw response (first items)</p>
                  <pre className="text-[11px] font-mono bg-[#0B0E14] text-[#E4E7EB] rounded-[4px] p-3 max-h-48 overflow-auto">
                    {JSON.stringify(testResult.resolvedArray ?? testResult.raw, null, 2)}
                  </pre>
                  {!testResult.resolvedArray && (
                    <p className="text-[11px] text-[#DD7A01] mt-1">
                      "Array path in response" didn't resolve to a list — adjust it above and test again.
                    </p>
                  )}
                </div>
                {testResult.preview && (
                  <div>
                    <p className="text-[11px] font-bold text-[#706E6B] mb-1">What your current mapping extracts</p>
                    <div className="border border-[#D8DDE6] rounded-[4px] overflow-hidden">
                      <div className="slds2-table-wrap overflow-x-auto">
                      <table className="w-full text-[11px]">
                        <thead className="text-[#706E6B] text-[11px] uppercase tracking-wide">
                          <tr>
                            <th className="text-left p-2">SKU</th>
                            <th className="text-left p-2">Name</th>
                            <th className="text-left p-2">Price</th>
                            <th className="text-left p-2">Currency</th>
                          </tr>
                        </thead>
                        <tbody>
                          {testResult.preview.map((p, i) => (
                            <tr key={i} className="border-t border-[#F3F2F2]">
                              <td className="p-2 font-mono">{String(p.sku ?? '—')}</td>
                              <td className="p-2">{String(p.name ?? '—')}</td>
                              <td className="p-2">{String(p.price ?? '—')}</td>
                              <td className="p-2">{String(p.currency ?? '—')}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      </div>
                    </div>
                    <p className="text-[11px] text-[#706E6B] mt-1">
                      Any "—" means that field's path didn't match anything in the raw response above — fix the mapping and test again.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </Modal>
    </DashboardModuleShell>
  );
}
